import React from 'react'
import { Box, Alert, Button, Typography } from '@mui/material'

interface ErrorBoundaryProps {
  children: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  componentStack: string | null
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, componentStack: null }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo)
    this.setState({ componentStack: errorInfo.componentStack || null })
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 8 }}>
          <Alert severity="error" sx={{ maxWidth: 800, width: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold">Something went wrong</Typography>
            <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {this.state.error?.message}
            </Typography>
            {this.state.componentStack && (
              <Typography variant="body2" sx={{ mt: 2, fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '0.7rem', color: 'text.secondary', maxHeight: 300, overflow: 'auto' }}>
                Component stack:{this.state.componentStack}
              </Typography>
            )}
          </Alert>
          <Button
            variant="outlined"
            color="info"
            onClick={() => {
              this.setState({ hasError: false, error: null, componentStack: null })
              window.location.reload()
            }}
          >
            Reload Page
          </Button>
        </Box>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
